import { createContext, createMemo, type ParentComponent } from "solid-js";
import { useTheme } from "./theme-provider";
import { THEME_EXTRA_VARS_MAP, type ThemeTokens, TOKEN_TO_CSS_MAP } from "@bso/shared";

interface ListThemeContextValue {
  tokens: () => ThemeTokens | null;
}

const ListThemeContext = createContext<ListThemeContextValue>();

// Type guard to safely access theme tokens without assertions
function isThemeKey(key: string, t: ThemeTokens): key is keyof ThemeTokens {
  return key in t;
}

export const ListThemeProvider: ParentComponent<{
  listId?: string | null;
  todoId?: string | null;
  important?: boolean | null;
  myDayDate?: string | null;
}> = (props) => {
  // Mode is NOT passed: set by `resolveScopedTokens`, the only place that
  // decides it. Previously each scope called `resolveThemeTokens` independently and
  // passed DEVICE preference; the same bug occurred in several files.
  const { allThemes, isBlur, tokens: globalTokens, isDark, resolveScopedTokens } = useTheme();

  const dbTheme = createMemo(() => {
    const rawData = allThemes();
    if (!rawData || rawData.length === 0) {
      return null;
    }

    const resolvedListId = (() => {
      const isSmartSlug =
        !props.listId || ["all", "important", "my-day", "planned", "inbox"].includes(props.listId);
      if (props.listId && !isSmartSlug) {
        return props.listId;
      }
      if (props.important) {
        return "important";
      }
      if (props.myDayDate) {
        return "my-day";
      }
      return props.listId;
    })();

    const globalMatch = rawData.find((r) => r.target_id === "global") ?? null;

    if (resolvedListId) {
      const listMatch = rawData.find((r) => r.target_id === resolvedListId);
      return listMatch ?? globalMatch;
    }

    return globalMatch;
  });

  // Last non-null tokens we resolved. When a PowerSync refetch makes `allThemes()`
  // (and thus `dbTheme()`/`globalTokens()`) transiently null mid-burst, we keep
  // these instead of returning null — otherwise `styleObject()` collapses to `{}`,
  // stripping the list's inline CSS vars and flashing the base/global theme. This
  // mirrors the "retain previous theme" guard in the global ThemeProvider.
  let lastTokens: ThemeTokens | null = null;

  const tokens = createMemo<ThemeTokens | null>(() => {
    const themeResult = dbTheme();
    const parent = globalTokens();

    const next =
      !themeResult || !parent
        ? parent
        : resolveScopedTokens(themeResult, {
            isBlurContext: isBlur(),
            parentTokens: themeResult.target_id === "global" ? parent : null,
          });

    if (next) {
      lastTokens = next;
      return next;
    }
    return lastTokens;
  });

  // Apply all mapped tokens to CSS variables dynamically in a scoped style block
  const styleObject = createMemo(() => {
    const t = tokens();
    if (!t) {
      return {};
    }

    const styles: Record<string, string> = {};

    // Apply all mapped tokens to CSS variables
    for (const [tokenKey, cssVar] of Object.entries(TOKEN_TO_CSS_MAP)) {
      let value: string | number | boolean | null | undefined = null;

      if (tokenKey === "themeId") {
        value = t.id;
      } else if (isThemeKey(tokenKey, t)) {
        const val = t[tokenKey];
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
          value = val;
        }
      }

      if (value === undefined || value === null || value === "") {
        continue;
      }

      let strVal = String(value);
      if (typeof value === "boolean") {
        strVal = value ? "1" : "0";
      }

      styles[cssVar] = strVal;

      // Expand to multi-mapped variables
      const extraVars = (THEME_EXTRA_VARS_MAP as Record<string, string[] | undefined>)[tokenKey];
      if (extraVars) {
        for (const ev of extraVars) {
          styles[ev] = strVal;
        }
      }
    }

    styles["--is-dark"] = t.isDark ? "1" : "0";
    styles["--is-blur"] = t.isBlur ? "1" : "0";

    return styles;
  });

  return (
    <ListThemeContext.Provider value={{ tokens }}>
      <div
        style={styleObject()}
        class={`${(tokens()?.isDark ?? isDark()) ? "contents dark" : "contents light"} text-foreground`}
        data-theme-scope="list"
      >
        {props.children}
      </div>
    </ListThemeContext.Provider>
  );
};
